import React from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/pageheader";
import { ArrowRight } from "lucide-react";
// Optional avatars — swap to your actual assets

import PatrikImg from "@/assets/images/patrik-p.png";
import PhilipImg from "@/assets/images/philip-p.png";
import MagnusImg from "@/assets/images/magnus-c.png";
import BBFamily from "@/assets/images/bb-family.png";
import CTAButton from "../components/button";
/* Local Container helper to match your layout */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

// Small helper for the avatar chip with paint‑stroke backdrop
const AvatarChip: React.FC<{
  src?: string;
  alt: string;
}> = ({ src, alt }) => (
  <div className="relative inline-flex items-center">
    {src ? (
      <img
        src={src}
        alt={alt}
        className="relative h-16 w-16 rounded-full object-cover ring-4 ring-white/10"
      />
    ) : (
      <div className="relative grid h-16 w-16 place-items-center rounded-full bg-white/10 ring-4 ring-white/10 text-white/80">
        {alt[0]}
      </div>
    )}
  </div>
);
const CURRENT_WORK = [
  {
    name: "Patrik",
    quote:
      "Leading the digital evolution of a global gym chain — guiding engineers, shaping architecture, and setting a long-term tech vision so millions can train without friction.",
    avatar: PatrikImg,
  },
  {
    name: "Magnus",
    quote:
      "Helping a multinational brand transform how developers build and collaborate — including rolling out a Backstage-powered developer portal that now empowers teams worldwide.",
    avatar: MagnusImg,
  },
  {
    name: "Philip",
    quote:
      "Designing seamless onboarding and checkout flows where milliseconds matter — creating front-end experiences that disappear into the background and just work.",
    avatar: PhilipImg,
  },
];

export default function AboutPage() {
  return (
    <main className="text-white">
      {/* Hero */}
      <section className="py-14">
        <Container>
          <PageHeader
            eyebrow="About"
            title="Designing digital progress since day one"
            text="We’re a thoughtful, product‑minded crew who value clarity, craft, and long‑term thinking. No layers. No noise. Just senior talent working directly with you to move things forward — one meaningful decision at a time."
          />
        </Container>
      </section>

      {/* Intro Statement */}
      <section className="pb-0 sm:pb-6">
        <Container>
          <p></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Text */}
            <div className="max-w-3xl text-lg sm:text-xl text-white/80 leading-relaxed">
              <p>
                Six years ago, we started with a straightforward idea: good
                digital products are built by the people who actually do the
                work — together, without noise in between.
                <br /> <br />
                No layers. No hand-offs. No inflated frameworks. Just designers,
                developers, and clients sitting close to the problem and moving
                with clarity. <br /> <br />
                In a market full of complexity and buzzwords, choosing a simpler
                path requires a certain mindset. A willingness to be{" "}
                <strong>bold</strong> enough to say no to over-architecture and
                process for process’ sake. And <strong>brave</strong> enough to
                take real ownership — to commit, to ship, to stand behind
                decisions.
                <br /> <br />
                We build teams around challenges, not hierarchies.
                <br />
                Every product, platform and design system we work on is treated
                with the same care: thoughtful strategy, honest collaboration,
                and the discipline to improve things one step at a time.
              </p>
            </div>

            {/* Image */}
            <div className="flex justify-center md:justify-end">
              <img
                src={BBFamily}
                alt="BoldBrave family"
                className="w-full max-w-md rounded-2xl shadow-xl object-cover"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* "Currently" section */}
      <section className="py-12 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {CURRENT_WORK.map((item) => (
              <article key={item.name} className="flex flex-col">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
                  {item.name} is currently
                </div>
                <blockquote className="mt-3 text-1xl sm:text-2xl   text-white">
                  “{item.quote}”
                </blockquote>
                <div className="mt-6">
                  <AvatarChip src={item.avatar} alt={item.name} />
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <div className="mt-12 text-center">
        <p className="mb-8">
          Want to be part of something bold? We work with great people,
          ambitious brands, and real challenges. Meet the team here.
        </p>
        <CTAButton href="/team">Meet the team</CTAButton>
      </div>
    </main>
  );
}
